import { z } from 'zod';
import { minorUnitsSchema } from './expected-price.js';
/**
 * Handoff 0012 §5 — the payment method a charged change is paid with, shown in the confirm step
 * ("Pay $48.00 now with Visa •••• 4242"), and the way back when a payment did not go through.
 *
 * Read from the billing provider when the dialog opens and never stored (handoff decision). It
 * carries NO provider identifier — a payment-method or invoice id is a third-party internal id
 * (docs/008 S7). Wording everywhere is "payment method", never "card": the default may be a bank
 * account.
 */
/** What kind of method it is. `bank_account` is a US bank account (ACH Direct Debit). */
export const PAYMENT_METHOD_KINDS = ['card', 'bank_account', 'link', 'other'];
/**
 * How a payment by this method settles. `immediate`: a card or Link — the charge succeeds or
 * fails while the request is open. `delayed`: anything else (a bank debit) — the payment is
 * `processing` for days, so a charged change is paid FIRST and applied when it clears.
 */
export const PAYMENT_SETTLEMENTS = ['immediate', 'delayed'];
export const paymentMethodDTOSchema = z.object({
    kind: z.enum(PAYMENT_METHOD_KINDS),
    /** A card's brand (`visa`), or a bank account's bank name. NULL when the provider names none. */
    brand: z.string().min(1).max(100).nullable(),
    last4: z.string().regex(/^\d{4}$/).nullable(),
    /** Link only: the account's email — Link exposes no card details. */
    email: z.string().max(320).nullable(),
    /** A card past its expiry month: it cannot be charged, so the dialog asks for another. */
    expired: z.boolean(),
    settlement: z.enum(PAYMENT_SETTLEMENTS),
});
/**
 * A charged change the provider could not collect and is holding (handoff 0012 §5): the change is
 * parked until its open invoice is paid (Try again), and is discarded when it expires.
 *
 * - `declined` — the payment method was refused. Update it, then Try again.
 * - `requires_action` — the bank asked the customer to authenticate (3-D Secure). The customer
 *   completes it on the provider's hosted invoice page (`hostedInvoiceUrl`); the change applies
 *   when the payment lands.
 */
export const PENDING_PAYMENT_STATES = ['declined', 'requires_action'];
export const pendingPaymentDTOSchema = z.object({
    state: z.enum(PENDING_PAYMENT_STATES),
    amountDue: minorUnitsSchema,
    currency: z.string().regex(/^[A-Z]{3}$/),
    /** The provider's hosted page for this invoice; NULL when it offers none. */
    hostedInvoiceUrl: z.string().url().nullable(),
});
/**
 * `GET /api/web/billing/payment-method` → `{ data: PaymentMethodStatusDTO | null }`. `null`: the
 * tenant has no billing account (it has never checked out), so nothing it confirms in the dialog
 * is charged to a stored method.
 */
export const paymentMethodStatusDTOSchema = z.object({
    /** The method a charge would use now; NULL when there is none. */
    method: paymentMethodDTOSchema.nullable(),
    /** A charged change waiting on its payment; NULL when there is none. */
    pendingPayment: pendingPaymentDTOSchema.nullable(),
});
/**
 * Where the provider's hosted page sends the browser back to: a path of THIS app, never an
 * absolute URL (the server prefixes its own configured origin, so the body cannot redirect
 * anywhere else). One leading `/`, no `//`, no backslash, no fragment.
 */
export const returnPathSchema = z
    .string()
    .min(1)
    .max(1024)
    .regex(/^\/(?![/\\])[A-Za-z0-9\-._~/?&=%:,+]*$/, 'must be a path of this app');
/** `POST /api/web/billing/payment-method/session` — open the provider's payment-method update. */
export const paymentMethodSessionRequestSchema = z.object({ returnPath: returnPathSchema }).strict();
/**
 * `POST /api/web/billing/payment/retry` — Try again: pays the invoice of the parked change, which
 * applies it. `expectedAmountDue` is the amount the dialog showed; a different open amount (the
 * parked change is not the one on screen) is refused, 409 `PENDING_PAYMENT_MISMATCH`.
 */
export const retryPaymentRequestSchema = z.object({ expectedAmountDue: minorUnitsSchema }).strict();
/** `details` of a 402 `PAYMENT_ACTION_REQUIRED`: where the customer authenticates the payment. */
export const paymentActionRequiredDetailsSchema = z.object({ hostedInvoiceUrl: z.string().url().nullable() });
export function parsePaymentActionRequiredDetails(details) {
    const parsed = paymentActionRequiredDetailsSchema.safeParse(details);
    return parsed.success ? parsed.data : null;
}
/**
 * Handoff 0012 §5 (option B) — a charged change paid FIRST by a bank debit, waiting for the payment
 * to clear (usually 3–5 business days) before it is applied. `GET /api/web/billing/pending-charge`
 * → `{ data: PendingChargedChangeDTO | null }`; `DELETE` cancels it (voids its invoice).
 */
export const PENDING_CHARGED_CHANGE_KINDS = ['plan_change', 'add_app', 'location_subscribe'];
export const pendingChargedChangeDTOSchema = z.object({
    kind: z.enum(PENDING_CHARGED_CHANGE_KINDS),
    /** The plan moved to or added; NULL for a location change. */
    planCode: z.string().min(1).nullable(),
    /** How many locations it subscribes; NULL for a plan or app change. */
    locationCount: z.number().int().min(1).nullable(),
    amountDue: minorUnitsSchema,
    currency: z.string().regex(/^[A-Z]{3}$/),
    /** ISO-8601: when it was confirmed. */
    createdAt: z.string().datetime(),
});
//# sourceMappingURL=payment-method.js.map